import { useState } from "react";
import { ArrowLeft, Loader2, Mail, KeyRound, Lock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AuthService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import heroImg from "@/assets/hero-landing.jpg";
import logoImg from "@/assets/logo.png";

type Step = "email" | "otp" | "password" | "done";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState(""); // signed token from backend
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  // Step 1 — send OTP, receive signed token
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await AuthService.sendOtp(email);
      const token = (res.data as any).otpToken;
      if (!token) throw new Error("Failed to generate OTP token. Please try again.");
      setOtpToken(token);
      toast({ title: "OTP sent", description: "Check your email for the 6-digit code." });
      setOtp("");
      setStep("otp");
    } catch (err: any) {
      toast({ title: "Error", description: err.response?.data?.error || "Something went wrong.", variant: "destructive" });
    }
    setLoading(false);
  };

  // Step 2 — verify OTP using the signed token
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await AuthService.verifyOtp(email, otp, otpToken);
      setStep("password");
    } catch (err: any) {
      toast({ title: "Invalid code", description: err.response?.data?.error || "The code is incorrect or has expired.", variant: "destructive" });
    }
    setLoading(false);
  };

  // Step 3 — reset password using the signed token + OTP
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "Too short", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await AuthService.resetPassword(email, otp, otpToken, password);
      setStep("done");
    } catch (err: any) {
      toast({ title: "Error", description: err.response?.data?.error || "Something went wrong.", variant: "destructive" });
    }
    setLoading(false);
  };

  const stepDots: Step[] = ["email", "otp", "password"];

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <img src={heroImg} alt="Background" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay" />

      <Link
        to="/login"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Login
      </Link>

      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-in">
        <div className="glass-login rounded-2xl p-8 md:p-10 shadow-2xl">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-xl p-2 shadow-lg">
              <img src={logoImg} alt="ACA" className="h-12 w-12 object-contain rounded-lg" />
            </div>
          </div>

          {/* Step indicator */}
          {step !== "done" && (
            <div className="flex justify-center gap-2 mb-6">
              {stepDots.map((s, i) => {
                const currentIdx = stepDots.indexOf(step);
                return (
                  <div
                    key={s}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      s === step ? "w-8 bg-warning" : i < currentIdx ? "w-4 bg-white/50" : "w-4 bg-white/20"
                    }`}
                  />
                );
              })}
            </div>
          )}

          {/* Step 1 — Email */}
          {step === "email" && (
            <>
              <div className="text-center mb-8">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-warning" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white">Forgot Password</h1>
                <p className="mt-2 text-white/50 text-sm">Enter your email — we'll send a 6-digit code</p>
              </div>
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/80 text-sm">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="input-glass h-11 rounded-lg"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg gradient-warm border-0 text-white font-semibold hover:opacity-90 transition-opacity"
                  disabled={loading}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : "Send OTP"}
                </Button>
              </form>
            </>
          )}

          {/* Step 2 — OTP */}
          {step === "otp" && (
            <>
              <div className="text-center mb-8">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <KeyRound className="h-6 w-6 text-warning" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white">Enter OTP</h1>
                <p className="mt-2 text-white/50 text-sm">
                  We sent a 6-digit code to<br />
                  <span className="text-white/70 font-medium">{email}</span>
                </p>
              </div>
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-white/80 text-sm">6-Digit Code</Label>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    required
                    autoFocus
                    className="input-glass h-12 rounded-lg tracking-[0.5em] text-center text-xl font-mono"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg gradient-warm border-0 text-white font-semibold hover:opacity-90 transition-opacity"
                  disabled={loading || otp.length < 6}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : "Verify Code"}
                </Button>
                <button
                  type="button"
                  className="w-full text-center text-sm text-white/40 hover:text-white/70 transition-colors"
                  onClick={() => { setOtp(""); setOtpToken(""); setStep("email"); }}
                >
                  Didn't receive it? Go back and resend
                </button>
              </form>
            </>
          )}

          {/* Step 3 — New Password */}
          {step === "password" && (
            <>
              <div className="text-center mb-8">
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <Lock className="h-6 w-6 text-warning" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white">New Password</h1>
                <p className="mt-2 text-white/50 text-sm">Choose a strong password for your account</p>
              </div>
              <form onSubmit={handleSetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/80 text-sm">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    autoFocus
                    className="input-glass h-11 rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-white/80 text-sm">Confirm Password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    required
                    className="input-glass h-11 rounded-lg"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 rounded-lg gradient-warm border-0 text-white font-semibold hover:opacity-90 transition-opacity"
                  disabled={loading}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : "Set New Password"}
                </Button>
              </form>
            </>
          )}

          {/* Done */}
          {step === "done" && (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-full bg-success/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-white font-semibold text-xl">Password Updated!</p>
              <p className="text-white/60 text-sm">You can now sign in with your new password.</p>
              <Button
                className="w-full h-11 rounded-lg gradient-warm border-0 text-white font-semibold hover:opacity-90"
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
